'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Bot, Sparkles, Smartphone } from 'lucide-react';
import { WhatsAppHeroCards } from '@/components/dashboard/whatsapp/WhatsAppHeroCards';
import {
  WhatsAppMessagesList,
  WhatsAppMessageItem
} from '@/components/dashboard/whatsapp/WhatsAppMessagesList';
import { WhatsAppMessagePreviewModal } from '@/components/dashboard/whatsapp/WhatsAppMessagePreviewModal';
import { TemplateEditDrawer } from '@/components/dashboard/whatsapp/TemplateEditDrawer';
import { WhatsAppQrModal } from '@/components/dashboard/whatsapp/WhatsAppQrModal';
import { WhatsAppChatsInbox } from '@/components/dashboard/whatsapp/WhatsAppChatsInbox';
import { ScheduledNotificationsQueue } from '@/components/dashboard/whatsapp/ScheduledNotificationsQueue';

const ALL_WHATSAPP_TEMPLATES: WhatsAppMessageItem[] = [
  // 1. Recordatorios y Confirmaciones
  {
    id: 'msg_created',
    category: 'reminders',
    title: 'Confirmación inmediata de turno',
    subtitle: 'Se envía al confirmar la reserva online o recepción',
    trigger: 'Inmediato',
    enabled: true,
    content: '¡Hola {{cliente}}! 🎉 Tu turno para *{{servicio}}* fue confirmado para el *{{fecha}}* a las *{{hora}}* con {{profesional}} en {{negocio}}. Te esperamos. Podés gestionar tu cita aquí: {{link_gestion}}'
  },
  {
    id: 'msg_rem_standard',
    category: 'reminders',
    title: 'Recordatorio preventivo de cita',
    subtitle: 'Sale 24 horas antes del turno agendado',
    trigger: '24hs antes',
    enabled: true,
    content: 'Hola {{cliente}} 👋 Te recordamos que mañana *{{fecha}}* tenés turno para *{{servicio}}* a las *{{hora}}* en {{negocio}}. Recordá llegar unos minutos antes.'
  },
  {
    id: 'msg_rem_confirmed',
    category: 'reminders',
    title: 'Recordatorio para cita confirmada',
    subtitle: 'Aviso especial cuando el cliente ya confirmó asistencia',
    trigger: 'Sale 10:00',
    enabled: true,
    content: '¡Hola {{cliente}}! Todo listo para tu turno de *{{servicio}}* hoy a las *{{hora}}*. Te espera {{profesional}} en {{negocio}}.'
  },
  {
    id: 'msg_rem_sameday',
    category: 'reminders',
    title: 'Recordatorio el mismo día',
    subtitle: 'Sale el día de la cita a las 08:00 hs',
    trigger: '08:00 AM',
    enabled: true,
    content: '¡Hola {{cliente}}! Hoy es tu cita en {{negocio}} a las *{{hora}}* para *{{servicio}}*. ¡Ya tenemos todo preparado!'
  },

  // 2. Modificaciones y Reagendamientos
  {
    id: 'msg_edit_business',
    category: 'updates',
    title: 'Cita editada por el negocio',
    subtitle: 'Disparo al reprogramar fecha, hora o staff desde el admin',
    trigger: 'Al editar',
    enabled: true,
    content: 'Hola {{cliente}}, te informamos que tu cita para *{{servicio}}* en {{negocio}} fue modificada. Nuevo horario: *{{fecha}}* a las *{{hora}}*. Podés ver los detalles aquí: {{link_gestion}}'
  },
  {
    id: 'msg_edit_client',
    category: 'updates',
    title: 'Cita editada por el cliente',
    subtitle: 'Confirmación tras autogestión desde el portal público',
    trigger: 'Al autogestionar',
    enabled: true,
    content: '¡Listo {{cliente}}! Tu reprogramación fue registrada con éxito: nos vemos el *{{fecha}}* a las *{{hora}}* para *{{servicio}}* en {{negocio}}.'
  },

  // 3. Cancelaciones y Expiraciones
  {
    id: 'msg_cancel_business',
    category: 'cancellations',
    title: 'Cita cancelada por el negocio',
    subtitle: 'Notificación cuando la administración anula el turno',
    trigger: 'Al cancelar',
    enabled: true,
    content: 'Hola {{cliente}}, lamentamos informarte que tu turno para el *{{fecha}}* a las *{{hora}}* en {{negocio}} tuvo que ser cancelado. Por favor comunicate con nosotros para coordinar una nueva fecha.'
  },
  {
    id: 'msg_cancel_client',
    category: 'cancellations',
    title: 'Cita cancelada por el cliente',
    subtitle: 'Confirmación de anulación y liberación del slot',
    trigger: 'Al anular',
    enabled: true,
    content: 'Hola {{cliente}}, confirmamos que tu turno para *{{servicio}}* el *{{fecha}}* ha sido cancelado y el espacio quedó liberado. ¡Esperamos verte pronto!'
  },
  {
    id: 'msg_cancel_deposit',
    category: 'cancellations',
    title: 'Cancelación por seña no abonada',
    subtitle: 'Liberación automática por vencimiento del plazo de Mercado Pago',
    trigger: 'Expiración',
    enabled: true,
    content: 'Hola {{cliente}}, tu reserva para *{{servicio}}* ha expirado debido a que no se registró el pago de la seña dentro del tiempo estipulado. El turno quedó liberado.'
  },

  // 4. Fidelización y Reseñas
  {
    id: 'msg_review_request',
    category: 'reviews',
    title: 'Solicitud de reseña post-servicio',
    subtitle: 'Sale 2 horas después de atendido el cliente',
    trigger: '+2hs post-cita',
    enabled: true,
    content: '¡Hola {{cliente}}! Esperamos que hayas disfrutado tu experiencia con *{{servicio}}* en {{negocio}}. ¿Nos dejarías una reseña rápida en Google? Tu opinión nos ayuda muchísimo a seguir mejorando ⭐'
  }
];

export default function WhatsAppPage() {
  const [activeTab, setActiveTab] = useState<'chats' | 'automatizacion'>('automatizacion');
  const [connected, setConnected] = useState(true);
  const [phone, setPhone] = useState('+54 9 11 3176-7548');
  const [messages, setMessages] = useState<WhatsAppMessageItem[]>(ALL_WHATSAPP_TEMPLATES);
  const [loading, setLoading] = useState(false);

  // Sincronizar tab con URL search param
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isChats = window.location.search.includes('tab=chats');
      setActiveTab(isChats ? 'chats' : 'automatizacion');
    }
  }, []);

  const handleSelectTab = (tab: 'chats' | 'automatizacion') => {
    setActiveTab(tab);
    if (typeof window !== 'undefined') {
      const newUrl = `${window.location.pathname}?tab=${tab}`;
      window.history.replaceState(null, '', newUrl);
    }
  };

  // Modals & Drawers state
  const [showQrModal, setShowQrModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppMessageItem | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const [resConfig, resInstance] = await Promise.all([
        fetch('/api/integrations/whatsapp', { signal: controller.signal }),
        fetch('/api/whatsapp/instance', { signal: controller.signal })
      ]);
      clearTimeout(timeoutId);

      const [dataConfig, dataInstance] = await Promise.all([
        resConfig.json(),
        resInstance.json()
      ]);

      if (dataInstance.success && dataInstance.instance) {
        const isConn = dataInstance.instance.connected || dataInstance.instance.status === 'CONNECTED';
        setConnected(isConn);
        if (dataInstance.instance.phone) {
          setPhone(dataInstance.instance.phone);
        }
      } else if (dataConfig.success) {
        setConnected(dataConfig.connected ?? false);
        if (dataConfig.phone) setPhone(dataConfig.phone);
      }
    } catch (err) {
      console.warn('Syncing whatsapp data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleToggleMessage = (id: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, enabled: !m.enabled } : m))
    );
  };

  const handleSaveTemplate = async (updated: WhatsAppMessageItem) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === updated.id ? updated : m))
    );
  };

  const handleDisconnect = async () => {
    if (!confirm('¿Deseas desvincular este número de WhatsApp?')) return;
    try {
      await Promise.all([
        fetch('/api/whatsapp/instance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'disconnect' })
        }),
        fetch('/api/integrations/whatsapp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'toggle_connection', connected: false })
        })
      ]);
      setConnected(false);
    } catch (err) {
      console.error('Error al desvincular WhatsApp:', err);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12 font-sans">
      {/* Selector de Experiencia: Chats vs Automatización */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-1 border-b border-slate-200/80">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            {activeTab === 'chats' ? (
              <>
                <MessageSquare className="w-5 h-5 text-[#25D366]" />
                <span>Bandeja de Chats de WhatsApp</span>
              </>
            ) : (
              <>
                <Bot className="w-5 h-5 text-[#6255F5]" />
                <span>WhatsApp y Automatizaciones</span>
              </>
            )}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {activeTab === 'chats'
              ? 'Bandeja de conversaciones en vivo con clientes y respuestas de confirmación.'
              : 'Conexión de línea, disparadores automáticos, plantillas transaccionales y cola de notificaciones.'}
          </p>
        </div>

        {/* Pestañas Segmentadas */}
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-2xl border border-slate-200 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => handleSelectTab('chats')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'chats'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5 text-[#25D366]" />
            <span>Chats en vivo</span>
          </button>

          <button
            type="button"
            onClick={() => handleSelectTab('automatizacion')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'automatizacion'
                ? 'bg-white text-[#6255F5] shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#6255F5]" />
            <span>Automatización (9)</span>
          </button>
        </div>
      </div>

      {activeTab === 'chats' ? (
        /* BANDEJA DE CONVERSACIONES EN VIVO */
        <WhatsAppChatsInbox />
      ) : (
        /* VISTA DE AUTOMATIZACIÓN Y PLANTILLAS */
        <div className="space-y-8">
          {/* 1. Tarjeta Hero de Conexión de Dispositivo (Evolution API / Baileys) */}
          <WhatsAppHeroCards
            connected={connected}
            phone={phone}
            onOpenQrModal={() => setShowQrModal(true)}
            onDisconnect={handleDisconnect}
          />

          {/* 2. Catálogo de Plantillas Nativas en Acordeones */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                  Plantillas Transaccionales de WhatsApp
                </h2>
                <p className="text-xs text-slate-500">
                  Configurá el texto, tokens dinámicos y disparadores para cada etapa del turno.
                </p>
              </div>
            </div>

            <WhatsAppMessagesList
              messages={messages}
              onToggleMessage={handleToggleMessage}
              onPreviewMessage={(msg) => setEditingTemplate(msg)}
              onEditMessage={(msg) => setEditingTemplate(msg)}
            />
          </div>

          {/* 3. Cola de Notificaciones Programadas */}
          <div className="space-y-4 pt-4 border-t border-slate-200/80">
            <div>
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                Cola de Envíos en Tiempo Real
              </h2>
              <p className="text-xs text-slate-500">
                Supervisión del despachador automático que procesa los mensajes del día.
              </p>
            </div>
            <ScheduledNotificationsQueue />
          </div>
        </div>
      )}

      {/* DRAWER LATERAL: Editor de Plantilla (480px) con Tokens y Previsualizador */}
      <TemplateEditDrawer
        isOpen={Boolean(editingTemplate)}
        onClose={() => setEditingTemplate(null)}
        template={editingTemplate}
        onSave={handleSaveTemplate}
      />

      {/* MODAL: QR de Vinculación WhatsApp */}
      <WhatsAppQrModal
        isOpen={showQrModal}
        onClose={() => setShowQrModal(false)}
        onConnected={fetchConfig}
      />
    </div>
  );
}
